import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Check, X } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { Money, TopupBadge, ReceiptLink, METHOD_LABEL, METHOD_ACCOUNT_KIND } from "@/components/wallet/shared";
import CashAccountSelect, { useCashAccounts } from "@/components/wallet/CashAccountSelect";

function DecisionDialog({ topup, mode, onClose }) {
  const qc = useQueryClient();
  const { data: accounts = [] } = useCashAccounts();
  const defaultAcc = accounts.find((a) => a.kind === METHOD_ACCOUNT_KIND[topup?.method])?.id || "";
  const [accountId, setAccountId] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const approve = mode === "approve";
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/wallet/topups/${topup.id}/${approve ? "approve" : "reject"}`, {
      account_id: approve ? ((accountId ?? defaultAcc) || null) : null,
      amount: approve && amount ? parseFloat(amount) : null, note: note || null,
    })).data,
    onSuccess: () => {
      toast.success(approve ? "Ricarica approvata e accreditata" : "Richiesta rifiutata");
      ["wallet-topups", "wallet-summary", "wallet-owners"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <Dialog open={!!topup} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{approve ? "Approva ricarica" : "Rifiuta ricarica"}</DialogTitle></DialogHeader>
        <div className="text-sm">{topup?.owner_name} · <Money value={topup?.amount}/> · {METHOD_LABEL[topup?.method]}</div>
        {approve && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Importo ricevuto (€)</Label><Input type="number" step="0.01" placeholder={String(topup?.amount ?? "")} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" data-testid="topup-approve-amount"/></div>
            <div><Label className="text-xs">Registra entrata in cassa</Label><CashAccountSelect value={accountId ?? defaultAcc} onChange={setAccountId} testId="topup-approve-account"/></div>
          </div>
        )}
        <div><Label className="text-xs">{approve ? "Nota (facoltativa)" : "Motivo"}</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid="topup-decision-note"/></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button className={approve ? "bg-[#0F766E] hover:bg-[#115E59]" : "bg-red-600 hover:bg-red-700"} disabled={mut.isPending} onClick={() => mut.mutate()} data-testid="topup-decision-confirm">
            {approve ? "Approva e accredita" : "Rifiuta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WalletRicariche() {
  const [status, setStatus] = useState("pending");
  const [decision, setDecision] = useState(null);
  const { data: topups = [], isLoading } = useQuery({
    queryKey: ["wallet-topups", status],
    queryFn: async () => (await api.get("/wallet/topups", { params: { status: status === "all" ? undefined : status } })).data,
  });
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto">
      <Link to="/wallet" className="text-sm text-stone-500 hover:text-[#0F766E] flex items-center gap-1 mb-4"><ArrowLeft size={14}/> Wallet</Link>
      <PageHeader title="Richieste di ricarica" subtitle="Verifica il pagamento esterno (bonifico, PayPal, Stripe) e accredita il wallet."/>
      <Tabs value={status} onValueChange={setStatus} className="mb-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="topups-tab-pending">In attesa</TabsTrigger>
          <TabsTrigger value="approved" data-testid="topups-tab-approved">Approvate</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="topups-tab-rejected">Rifiutate</TabsTrigger>
          <TabsTrigger value="all" data-testid="topups-tab-all">Tutte</TabsTrigger>
        </TabsList>
      </Tabs>
      <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
        {isLoading && <div className="p-8 text-center text-sm text-stone-400">Caricamento…</div>}
        {!isLoading && topups.length === 0 && <div className="p-8 text-center text-sm text-stone-400">Nessuna richiesta.</div>}
        <ul className="divide-y divide-stone-100">
          {topups.map((t) => (
            <li key={t.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`topup-${t.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{t.owner_name}</span>
                  <span className="text-[10px] text-stone-500 uppercase">{t.owner_type === "teacher" ? "Docente" : "Studente"}</span>
                  <TopupBadge status={t.status}/>
                </div>
                <div className="text-[11px] text-stone-500 mt-0.5">
                  {METHOD_LABEL[t.method] || t.method}{t.reference && ` · Rif. ${t.reference}`} · {fmtDateTime(t.created_at)}
                  {t.note && ` · "${t.note}"`}{t.decision_note && ` · Esito: ${t.decision_note}`}
                </div>
                <ReceiptLink topup={t}/>
              </div>
              <Money value={t.approved_amount ?? t.amount} className="font-semibold text-lg"/>
              {t.status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => setDecision({ topup: t, mode: "reject" })} data-testid={`topup-reject-${t.id}`}><X size={14}/></Button>
                  <Button size="sm" className="bg-[#0F766E] hover:bg-[#115E59]" onClick={() => setDecision({ topup: t, mode: "approve" })} data-testid={`topup-approve-${t.id}`}><Check size={14} className="mr-1"/> Approva</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Card>
      {decision && <DecisionDialog topup={decision.topup} mode={decision.mode} onClose={() => setDecision(null)}/>}
    </div>
  );
}
